import React from 'react';

import {
	GetObjectCommand,
	PutObjectCommand,
	S3Client,
} from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { logout } from '../redux/actions/auth';
import { store } from '../redux/store/store';
import { Auth } from './amplifyAuthCompat';
import 'moment/locale/es';
import { createRoot } from 'react-dom/client';

import { ModalWebsocket } from '../pages/Admin/ModalWebsocket';
import { websocket } from '../redux/actions/websocket';

const getS3Client = (): S3Client => {
	const region = process.env.REACT_APP_REGION as string;
	const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID as string;

	return new S3Client({
		region,
		credentials: fromCognitoIdentityPool({
			clientConfig: { region },
			identityPoolId,
		}),
	});
};

export const setClass = (text: string) => text.trim().replace(/\s\s+/g, ' ');

export const isISODate = (str: unknown): boolean => {
	const s = String(str);
	if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(s)) {
		return false;
	}
	const d = new Date(s);
	return d.toISOString() === s;
};

export const returnValidDate = (str: unknown, opc: number): string | null => {
	// opc = 1 => to show
	// opc = 2 => to insert

	if (str === '' || str === null || str === undefined) {
		return null;
	}

	let s = String(str);

	if (isISODate(s)) {
		return opc === 1 ? s.substring(0, 10) : s.slice(0, 19).replace('T', ' ');
	}

	// is spanish date
	if (s.slice(0, 10).indexOf('/') > -1) {
		const spadate = s.slice(0, 10).split('/');
		s = `${spadate[2]}-${spadate[1]}-${spadate[0]}`;
	}

	const date = new Date(s);
	if (date.toString() !== 'Invalid Date') {
		return opc === 1
			? date.toISOString().substring(0, 10)
			: date.toISOString().slice(0, 19).replace('T', ' ');
	}

	return s;
};

export const capitalizeWords = (text: string): string => {
	return text
		.toLowerCase()
		.replace(/(?:^|\s|['`‘’.-])[^\x20-\x60^\x7B-\xDF](?!(\s|$))/g, (a) =>
			a.toUpperCase(),
		);
};

export const closeSession = (
	_wsClient: unknown = null,
	redirect = true,
): void => {
	// let history = useHistory();
	Auth.signOut().then(() => {
		store.dispatch(logout());
		if (redirect) {
			globalThis.location.href = '/auth';
		}
	});
};

export const cleanProperties = (
	obj: Record<string, unknown>,
): Record<string, unknown> => {
	return Object.keys(obj).reduce((acc, key) => {
		// if (obj[key] !== "") {
		if (obj[key] || obj[key] === 0) {
			acc[key] = obj[key];
		}
		return acc;
	}, {});
};

export const fieldError = (
	errors: Record<string, unknown>,
	text = '',
): unknown => {
	const arr = text.split('.');

	if (arr.length <= 0) {
		return false;
	}

	if (arr.length === 1) {
		return errors[arr[0]];
	} else {
		return errors[arr[0]]?.[arr[1]]?.[arr[2]];
	}
};

export const validFile = (file: unknown): boolean => {
	const allowedFiles = [
		'.xls',
		'.xlsx',
		'.doc',
		'.docx',
		'.pdf',
		'.txt',
		'.jpeg',
		'.jpg',
		'.png',
		'.zip',
		'.rar',
		'.msg',
	];
	const rgx = new RegExp(
		`([a-záéíóúñ0-9\\s_\\.\\-\\(\\)])+(${allowedFiles.join('|')})$`,
	);
	return rgx.test(file.toString().toLowerCase());
};

export const getFileExtension = (str: string): string =>
	str.slice(str.lastIndexOf('.'));

export const uploadFileToS3 = async (
	fileObj: File,
	path: string,
	fileName: string,
): Promise<string> => {
	const s3 = getS3Client();
	const bucket = process.env.REACT_APP_S3_BUCKET as string;
	const key = `${path}/${fileName}`;
	const command = new PutObjectCommand({
		Bucket: bucket,
		Key: key,
		ContentType: fileObj.type,
	});
	const url = await getSignedUrl(s3, command, { expiresIn: 60 * 2 });

	return fetch(url, {
		method: 'PUT',
		headers: {
			'Conten-Type': fileObj.type,
		},
		body: fileObj,
	}).then((response) => {
		if (!response.ok) {
			throw new Error(`${response.status}: ${response.statusText}`);
		}
		return url.split('?')[0];
	});
};

export const downloadFileFromS3 = async (s3Key: string): Promise<string> => {
	const s3 = getS3Client();
	const bucket = process.env.REACT_APP_S3_BUCKET as string;
	const command = new GetObjectCommand({
		Bucket: bucket,
		Key: s3Key,
	});
	return getSignedUrl(s3, command, { expiresIn: 60 * 2 });
};

export const QuarterValidate = () => {
	const { etapa, grupos } = useSelector((state) => state.uReducer);
	return etapa === 1 || grupos.includes('admin');
};

export const emptyObj = (obj: Record<string, unknown>): boolean => {
	return Object.keys(obj).length === 0;
};

// custom hook
export const useSessionValidations = () => {
	const { etapa, grupos } = useSelector((state) => state.uReducer);
	const listaBlanca = !!grupos.includes('admin');
	const listaStaffing = !!grupos.includes('staffing');
	const quarterValidate = etapa === 1 || grupos.includes('admin');

	return { listaBlanca, listaStaffing, quarterValidate };
};

export const wsActions = (opc = 0): WebSocket => {
	const wsClient = new WebSocket(process.env.REACT_APP_WS_URL);
	wsClient.onopen = () => {
		if (wsClient.readyState === wsClient.OPEN) {
			Auth.currentAuthenticatedUser().then((user) => {
				switch (opc) {
					case 0:
						wsClient.send(
							JSON.stringify({
								action: 'message',
								message: JSON.stringify({
									opc: 0,
									user: user.attributes.email,
								}),
							}),
						);
						break;
					case 1:
						wsClient.send(
							JSON.stringify({
								action: 'message',
								message: JSON.stringify({
									opc: 1,
									user: user.attributes.email,
								}),
							}),
						);
						// console.log(
						//   `El administrador ha cambiado estado del ejercicio at ${new Date()}`
						// );
						break;
				}
			});
		}
	};

	wsClient.onmessage = (message) => {
		const json = JSON.parse(message.data);

		switch (json.opc) {
			case 0:
				// console.log(
				//   `MESSAGE for ALL >>> Nuevo websocket conectado at ${new Date()}`
				// );
				break;
			case 1:
				// console.log(
				//   `MESSAGE for ALL >>> Ejercicio modificado por el Admin at ${new Date()}`
				// );
				Auth.currentAuthenticatedUser().then((_user) => {
					// if (user.attributes.email !== json.user) {
					// NOTE: no se reutiliza closeSession() xq sino no se mostraría el modal-mensaje
					Auth.signOut().then(() => {
						store.dispatch(logout());
					});
					const element = React.createElement(ModalWebsocket);
					const container = document.getElementById('root');
					const root = createRoot(container);
					root.render(element);
					// }
				});
				break;
		}
	};

	wsClient.onclose = () => {
		console.log(`Este cliente se DES-conectó at ${new Date()}`);
		Auth.currentAuthenticatedUser().then((_user) => {
			const wsClient2 = wsActions();
			// console.log(
			//   `Este cliente se RE-conectó at ${new Date()} con el nuevo websocket => `,
			//   wsClient2
			// );
			store.dispatch(websocket(wsClient2)); // NOTE: al parecer los websocket solo duran 10 min aprox en el navegador si es q la pestaña no se encuentra activa
		});
	};

	return wsClient;
};

// Devuelve la fecha en formato YYYY/MM/DD
export const formatDate = (date: unknown): string | null =>
	date ? moment(date as string).format('YYYY-MM-DD') : null;

// Devuelve la fecha en formato DD-MM-YYYY
export const formatDateDMY = (date: unknown): string | null =>
	date ? moment(date as string).format('DD-MM-YYYY') : null;

// Devuelve la fecha en formato DD/MM/YYYY
export const formatDateDMY2 = (date: unknown): string | null =>
	date ? moment(date as string).format('DD/MM/YYYY') : null;

// Permite devolver las fechas desde el primer dia del mes anterior según ejercicio actual para validación fecha Deseable para creación/edición solicitud en Staffing
export const getDatePreviousMonthByEjercicio = (ejercicioActual: {
	MesAnterior: string;
	Anio: number;
}): Date => {
	const now = new Date(); // Fecha actual
	const listMonths = moment.months();
	let anioActual = now.getFullYear();

	//index del mes anterior del ejercicio actual
	const getIndexMonthSelected = listMonths.indexOf(
		ejercicioActual.MesAnterior.toLowerCase(),
	);

	const firstDay = 1;
	// [11] es Diciembre
	if (ejercicioActual.Anio < anioActual || getIndexMonthSelected === 11) {
		// Si el año en curso es igual que el año del ejercio actual retrocedemos un año
		anioActual = anioActual - 1;
	}
	return new Date(anioActual, getIndexMonthSelected, firstDay);
};

// Permite devolver las fechas habilitadas desde el dia siguiente al actual
export const getDateToDayNext = (): Date => {
	const now = new Date(); // Fecha actual
	return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
};

export const redirectTo = (path = '/'): void => {
	const base = process.env.REACT_APP_PUBLIC_URL?.replace(/\/+$/, '') || '';
	const cleanPath = path.startsWith('/') ? path : `/${path}`;
	const finalUrl = `${base}${cleanPath}`;

	globalThis.location.assign(finalUrl);
};
